import { Vector2, Vector3, Web3DViewer } from '@technology/web3d';
import { createSequentialEffect } from '@trimble/solidjs-utils';
import { Point2D } from '@trimble/utilities';
import {
  CapabilityCoordinatorBase,
  CapabilityRegistryContainer,
} from '@trimble/web3d';
import { DEFAULT_CAMERA_HEIGHT } from '@trimble/web3d-capabilities';
import {
  TileMapCapabilityProvider,
  TileMapProviderProps,
} from '@trimble/web3d-providers';
import { getLogger } from '@trimble/logging';
import {
  Accessor,
  createMemo,
  createRoot,
  createSignal,
  Setter,
} from 'solid-js';

const MODEL_ID = 'mm-satellite-map';
const BOUNDING_EXTENT = 100000;

// TODO: IMPORTANT - Put url including auth token in config!
const MAP_TILES_URL =
  'https://pcmiler.alk.com/apis/rest/v1.0/Service.svc/maptile?X={x}&Y={y}&Z={z}&Style=Satellite&dataset=PCM_EU&authtoken=37B2C5A6BE5CC7459137920B69EF6F3F';

export type FetchGeoDataFiles = (
  csib64: string
) => Promise<Map<string, ArrayBuffer>>;

export class TileMapCoordinatesCapabilityCoordinator extends CapabilityCoordinatorBase<CapabilityRegistryContainer> {
  private readonly tileMapProviderPropsSignal: Accessor<
    TileMapProviderProps | undefined
  >;
  private readonly setTileMapProviderProps: Setter<
    TileMapProviderProps | undefined
  >;

  private readonly contentUpdatedSignal: Accessor<void>;
  private readonly triggerContentUpdate: Setter<void>;

  private readonly dispose: () => void;

  constructor(
    protected readonly registryContainer: CapabilityRegistryContainer,
    private readonly viewer: Web3DViewer,
    private readonly fetchGeoDataFiles: FetchGeoDataFiles,
    /** Csib of the currently loaded dataset, from DataContext. */
    private readonly datasetCsib: Accessor<string | undefined>,
    /** World-coordinate center of the currently loaded dataset, from DataContext. */
    private readonly datasetCenter: Accessor<Point2D | undefined>
  ) {
    super(registryContainer);

    const [tileMapProviderProps, setTileMapProviderProps] =
      createSignal<TileMapProviderProps>();
    this.tileMapProviderPropsSignal = tileMapProviderProps;
    this.setTileMapProviderProps = setTileMapProviderProps;

    const [contentUpdated, triggerContentUpdate] = createSignal<void>(
      undefined,
      { equals: false }
    );
    this.contentUpdatedSignal = contentUpdated;
    this.triggerContentUpdate = triggerContentUpdate;

    this.dispose = createRoot((dispose) => {
      const geoReference = createMemo(() => {
        const c = this.datasetCsib();
        const ce = this.datasetCenter();
        if (c === undefined || ce === undefined) return undefined;
        return { csib: c, center: ce };
      });

      // On every geoReference change: unregister the existing TileMapCapabilityProvider,
      // then — if the new value is defined — register a fresh one via setupTileMap.
      // This ensures projection options always reflect the current csib.
      // eslint-disable-next-line solid/reactivity
      createSequentialEffect(geoReference, async (geo) => {
        await this.registryContainer.unregister(TileMapCapabilityProvider);

        if (geo === undefined) {
          this.triggerContentUpdate();
          return;
        }

        await this.setupTileMap(geo.csib, geo.center);
      });

      // Position the 2D map camera directly. Using the sync coordinator would
      // leak the geo-reference center into cross-pane sync and move
      // 3D panes on every point-cloud load.
      createSequentialEffect(
        () => geoReference()?.center,
        async (center) => {
          if (center === undefined) {
            return;
          }
          this.viewer.camera.position = new Vector3(
            center.x,
            center.y,
            DEFAULT_CAMERA_HEIGHT
          );
        }
      );

      return dispose;
    });
  }

  /** Registers a fresh TileMapCapabilityProvider for the given csib/center. */
  private async setupTileMap(csib: string, center: Point2D): Promise<void> {
    let geoDataFiles: Map<string, ArrayBuffer> = new Map();
    try {
      geoDataFiles = await this.fetchGeoDataFiles(csib);
    } catch (error) {
      getLogger().error('Failed to fetch geodata files for CSIB', error);
    }

    const loadLimitBounds: [Vector2, Vector2] = [
      new Vector2(center.x - BOUNDING_EXTENT, center.y - BOUNDING_EXTENT),
      new Vector2(center.x + BOUNDING_EXTENT, center.y + BOUNDING_EXTENT),
    ];

    const props: TileMapProviderProps = {
      projection: {
        csib,
        geoDataFiles,
      },
      loadLimitBounds: loadLimitBounds,
    };
    this.setTileMapProviderProps(props);

    const tileMapProvider = this.registryContainer.register(
      TileMapCapabilityProvider,
      new TileMapCapabilityProvider(
        this.viewer,
        this.tileMapProviderPropsSignal
      )
    );

    await tileMapProvider.addMapModel({
      baseUrl: MAP_TILES_URL,
      modelId: MODEL_ID,
      projection: props.projection,
      initialPosition: [center.x, center.y],
    });
    tileMapProvider.setSelectedItemId(MODEL_ID);
    this.triggerContentUpdate();
  }

  public get contentUpdated(): Accessor<void> {
    return this.contentUpdatedSignal;
  }

  public async cleanup(): Promise<void> {
    this.dispose();
    await this.registryContainer.unregister(TileMapCapabilityProvider);
  }
}
